import type { ChartDataPoint } from '@gonfalon/dataviz-experimental/types/ChartDataPoint';
import type { ChartDataResponse } from '@gonfalon/openapi/types';

import { ONE_DAY_MS } from '#components/AiMonitoring/util/constants';

export const TRAFFIC_TARGET_FIELD = 'cumulative_count';

export function isChartDataResponse(data: unknown): data is ChartDataResponse {
  return typeof data === 'object' && data !== null && 'fields' in data && 'items' in data;
}

export function getCustomTrafficChartInterval(startMs: number, endMs: number): 'hour' | 'day' {
  const duration = endMs - startMs;
  if (duration <= 14 * ONE_DAY_MS) {
    return 'hour';
  }
  return 'day';
}

/**
 * Fills in missing series values at each point with the last known cumulative
 * count, so every treatment renders a continuous, monotonically increasing line
 * even when a bucket has no new exposures for that treatment. Mutates and
 * returns the given points.
 */
export function backfillCumulativeSeriesKeys(points: ChartDataPoint[], seriesKeys: string[]): ChartDataPoint[] {
  if (points.length === 0 || seriesKeys.length === 0) {
    return points;
  }

  const lastKnown: Record<string, number> = {};
  for (const key of seriesKeys) {
    lastKnown[key] = 0;
  }

  for (const point of points) {
    for (const key of seriesKeys) {
      const value = point[key];
      if (typeof value === 'number') {
        lastKnown[key] = value;
      } else {
        point[key] = lastKnown[key];
      }
    }
  }

  return points;
}

/**
 * Replaces treatment IDs with readable names in a dataviz chart data response.
 */
export function injectChartDataWithTreatmentNames(
  chartData: ChartDataResponse,
  treatments: Array<{ _id?: string | null; name?: string | null }>,
) {
  const newItems = chartData.items.map((item) => {
    const treatmentId = item.group_by_values?.[0];
    const treatmentName = treatments.find((t) => t._id === treatmentId)?.name ?? treatmentId;
    return {
      ...item,
      group_by_values: [treatmentName],
    };
  });

  return {
    ...chartData,
    items: newItems,
  };
}
